import type { FilterType } from "@/components/@types";
import type { FrequencyEnum } from "@/__generated__/graphql";
import { ReportAmountOperator } from "@/__generated__/graphql";
import {
  getReportsFilters,
  resolveWidgetEntityIdentifiers,
  type ReportLayoutWidget,
} from "@/app/app/_/views/ReportsView/ReportsView.utils";
import {
  filterTypesToSharedOutputs,
  mergeSharedFilterOutputs,
} from "@/app/share/report/sharedReportFilters";
import type { SharedFilterOutput } from "@/app/share/report/sharedReportTypes";

const REPORT_ONLY_FILTER_KEYS = new Set(["frequency", "mode"]);

type ReportLayoutFilterLike = {
  key: string;
  type: string;
  operator: string;
  value: unknown;
  isLocked?: boolean;
};

export type SharedReportTimeframeFilterSetInput = {
  dates?: { start: string; end: string } | null;
  frequency?: FrequencyEnum | null;
  entityIdentifiers?: string[] | null;
};



export type WidgetFilterSets = {
  timeframeFilterSet?: SharedReportTimeframeFilterSetInput | null;
};

function reportLayoutFiltersToSharedOutputs(
  filters: ReportLayoutFilterLike[],
): SharedFilterOutput[] {
  return filters
    .filter(
      (filter) =>
        filter.isLocked !== true &&
        filter.key !== "entities" &&
        filter.operator != null &&
        filter.value != null &&
        filter.value !== "",
    )
    .map((filter) => ({
      key: filter.key,
      type: filter.type,
      operator: String(filter.operator),
      value: filter.value,
      isLocked: false,
    }));
}

function defaultFiltersToSharedOutputs(
  widget: ReportLayoutWidget,
): SharedFilterOutput[] {
  return (widget.defaultFilters ?? []).map((filter) => ({
    key: filter.key,
    type: filter.type,
    operator: filter.operator,
    value: filter.value,
    isLocked: Boolean(filter.isLocked),
  }));
}

function isObjectTableWidget(widget: ReportLayoutWidget): boolean {
  const presentation = widget.presentation;
  return (
    presentation === "TABLE" ||
    presentation === "GROUPED_SUMMARY"
  );
}

function reportFiltersToSharedOutputs(
  filters: FilterType[],
): SharedFilterOutput[] {
  const effective = getReportsFilters(filters);
  return filterTypesToSharedOutputs(effective).filter(
    (filter) => !REPORT_ONLY_FILTER_KEYS.has(filter.key),
  );
}

function widgetFiltersToSharedOutputs(
  filters: ReportLayoutFilterLike[] | FilterType[],
): SharedFilterOutput[] {
  if (!filters.length) return [];
  const sample = filters[0];
  if (
    "type" in sample &&
    typeof sample.type === "string" &&
    sample.type === sample.type.toLowerCase()
  ) {
    return filterTypesToSharedOutputs(filters as FilterType[]);
  }
  return reportLayoutFiltersToSharedOutputs(
    filters as ReportLayoutFilterLike[],
  );
}

function parseDateRange(value: unknown): { start: string; end: string } | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const start = record.date1 ?? record.start;
  const end = record.date2 ?? record.end;
  if (typeof start !== "string" || typeof end !== "string") return null;
  return { start, end };
}


function buildTimeframeFilterSet(
  merged: SharedFilterOutput[],
  entityIdentifiers: string[],
): SharedReportTimeframeFilterSetInput {
  const filterSet: SharedReportTimeframeFilterSetInput = { entityIdentifiers };
  for (const filter of merged) {
    if (filter.key === "dates") {
      const dates = parseDateRange(filter.value);
      if (dates) filterSet.dates = dates;
    } else if (filter.key === "frequency" && filter.value != null) {
      filterSet.frequency = String(filter.value) as FrequencyEnum;
    }
  }
  return filterSet;
}

/** Build typed widget filter sets for ``node(id:)`` widget content queries. */
export function buildWidgetFilterSet({
  widget,
  globalFilters,
  layoutEntityIdentifiers,
  widgetFilters,
}: {
  widget: ReportLayoutWidget;
  globalFilters: FilterType[];
  layoutEntityIdentifiers: string[];
  widgetFilters: ReportLayoutFilterLike[] | FilterType[];
}): WidgetFilterSets {
  const defaults = defaultFiltersToSharedOutputs(widget);
  let globalOutputs = reportFiltersToSharedOutputs(globalFilters);
  if (isObjectTableWidget(widget)) {
    globalOutputs = globalOutputs.filter(
      (filter) => !REPORT_ONLY_FILTER_KEYS.has(filter.key),
    );
  }
  const widgetOutputs = widgetFiltersToSharedOutputs(widgetFilters);
  const merged = mergeSharedFilterOutputs(
    defaults,
    globalOutputs,
    widgetOutputs,
  );
  const entityIdentifiers = resolveWidgetEntityIdentifiers(
    widget,
    layoutEntityIdentifiers,
  );

  // One filter set for every widget: a source is named in lower case now
  // ("transactions"), so the branches that compared it against "TRANSACTIONS"
  // had stopped matching anything - and the query they fed had already
  // stopped declaring the variables they built.
  return {
    timeframeFilterSet: buildTimeframeFilterSet(merged, entityIdentifiers),
  };
}

